import React, { useEffect, useState } from 'react';
import { Edit3, History, LockKeyhole, RefreshCw } from 'lucide-react';
import { AdminPagination } from '../components/admin/AdminPagination';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminService } from '../services/adminService';
import { yikiService } from '../services/yikiService';
import { soiDaoService } from '../services/soiDaoService';
import { getApiErrorMessage } from '../utils/apiError';
import { parseSoiDaoLockResult } from '../utils/soiDaoUtils';

const PAGE_SIZE = 20;
const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-red';
const emptyForm = {
  gameType: 'lottery',
  roomId: '',
  first: '',
  three: '',
  two: '',
  result: '',
  results: ['', '', '', '', ''],
  note: '',
};

function resultLabel(lock) {
  if (lock.gameType === 'lottery') {
    return `${lock.resultFirst} / ${lock.resultThree} / ${lock.resultTwo}`;
  }
  if (lock.gameType === 'soi_dao') {
    const numbers = parseSoiDaoLockResult(lock);
    return numbers.length ? numbers.join(' · ') : lock.result;
  }
  if (lock.gameType === 'lao') {
    return lock.result;
  }
  return lock.result;
}

function roomsForGame(gameType, yikiRooms, soiDaoRooms) {
  if (gameType === 'yiki') return yikiRooms;
  if (gameType === 'soi_dao') return soiDaoRooms;
  return [];
}

export function AdminDrawLocksPage() {
  const [rows, setRows] = useState(null);
  const [yikiRooms, setYikiRooms] = useState([]);
  const [soiDaoRooms, setSoiDaoRooms] = useState([]);
  const [filters, setFilters] = useState({ gameType: '', roomId: '', status: '' });
  const [offset, setOffset] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [audits, setAudits] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      adminService.getDrawLocks({
        ...filters,
        limit: PAGE_SIZE,
        offset,
      }),
      yikiService.getRooms(),
      soiDaoService.getRooms(),
    ])
      .then(([lockData, yikiRoomData, soiDaoRoomData]) => {
        if (!cancelled) {
          setRows(lockData);
          setYikiRooms(yikiRoomData);
          setSoiDaoRooms(soiDaoRoomData);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, 'โหลดรายการล็อกไม่สำเร็จ'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, offset, refreshKey]);

  const changeFilter = (key, value) => {
    setOffset(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const startEdit = (lock) => {
    setEditing(lock);
    const soiResults = parseSoiDaoLockResult(lock);
    setForm({
      gameType: lock.gameType,
      roomId: lock.roomId || '',
      first: lock.resultFirst || '',
      three: lock.resultThree || '',
      two: lock.resultTwo || '',
      result:
        lock.gameType === 'yiki' || lock.gameType === 'lao'
          ? lock.result || ''
          : '',
      results:
        lock.gameType === 'soi_dao'
          ? Array.from({ length: 5 }, (_, index) => soiResults[index] || '')
          : ['', '', '', '', ''],
      note: lock.note || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setSoiResult = (index, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    setForm((current) => {
      const results = [...current.results];
      results[index] = digits;
      return { ...current, results };
    });
  };

  const submit = (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    let payload;
    if (form.gameType === 'lottery') {
      payload = {
        ...(editing ? {} : { gameType: 'lottery' }),
        first: form.first,
        three: form.three,
        two: form.two,
        note: form.note,
      };
    } else if (form.gameType === 'soi_dao') {
      const results = form.results.map((value) => value.padStart(2, '0'));
      if (results.some((value) => !/^\d{2}$/.test(value))) {
        setError('กรอกเลขสอยดาว 5 ช่อง เป็น 00-99');
        setSaving(false);
        return;
      }
      if (new Set(results).size !== 5) {
        setError('เลขสอยดาวทั้ง 5 ช่องต้องไม่ซ้ำกัน');
        setSaving(false);
        return;
      }
      payload = {
        ...(editing ? {} : { gameType: 'soi_dao', roomId: form.roomId }),
        results,
        note: form.note,
      };
    } else if (form.gameType === 'lao') {
      payload = {
        ...(editing ? {} : { gameType: 'lao' }),
        result: form.result,
        note: form.note,
      };
    } else {
      payload = {
        ...(editing ? {} : { gameType: 'yiki', roomId: form.roomId }),
        result: form.result,
        note: form.note,
      };
    }

    const request = editing
      ? adminService.updateDrawLock(editing.id, payload)
      : adminService.createDrawLock(payload);
    request
      .then(() => {
        setMessage(editing ? 'แก้ไขผลและบันทึก audit แล้ว' : 'ล็อกผลงวดถัดไปแล้ว');
        setEditing(null);
        setForm(emptyForm);
        setRefreshKey((key) => key + 1);
      })
      .catch((err) => setError(getApiErrorMessage(err, 'บันทึกไม่สำเร็จ')))
      .finally(() => setSaving(false));
  };

  const showAudits = (lock) => {
    adminService
      .getDrawLockAudits(lock.id)
      .then((items) => setAudits({ lock, items }))
      .catch((err) => setError(getApiErrorMessage(err, 'โหลด audit ไม่สำเร็จ')));
  };

  const formRooms = roomsForGame(form.gameType, yikiRooms, soiDaoRooms);
  const filterRooms = roomsForGame(
    filters.gameType,
    yikiRooms,
    soiDaoRooms,
  );
  const allRooms = [...yikiRooms, ...soiDaoRooms];
  const selectedSoiRoom = soiDaoRooms.find(
    (room) => room.id === form.roomId,
  );

  return (
    <div className="space-y-6">
      <Card className="border-2 border-brand-red/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockKeyhole className="h-5 w-5" />
            {editing ? `แก้ไขล็อก #${editing.id}` : 'ล็อกผลรอบถัดไป'}
          </CardTitle>
          <p className="text-sm text-gray-500">
            แก้ไขได้เฉพาะก่อนเริ่มออกรางวัล ทุกการแก้ไขเก็บ audit
          </p>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 md:grid-cols-6" onSubmit={submit}>
            <select
              className={inputClass}
              value={form.gameType}
              disabled={Boolean(editing)}
              onChange={(e) =>
                setForm({ ...emptyForm, gameType: e.target.value })
              }
            >
              <option value="lottery">Lottery</option>
              <option value="lao">หวยลาว</option>
              <option value="yiki">Yiki</option>
              <option value="soi_dao">Soi Dao</option>
            </select>
            {form.gameType === 'lottery' ? (
              <>
                <input
                  className={inputClass}
                  maxLength={6}
                  placeholder="รางวัลที่ 1 (6 หลัก)"
                  value={form.first}
                  onChange={(e) => setForm({ ...form, first: e.target.value })}
                  required
                />
                <input
                  className={inputClass}
                  maxLength={3}
                  placeholder="3 หลัก"
                  value={form.three}
                  onChange={(e) => setForm({ ...form, three: e.target.value })}
                  required
                />
                <input
                  className={inputClass}
                  maxLength={2}
                  placeholder="2 หลัก"
                  value={form.two}
                  onChange={(e) => setForm({ ...form, two: e.target.value })}
                  required
                />
              </>
            ) : null}
            {form.gameType === 'lao' ? (
              <input
                className={inputClass}
                maxLength={6}
                placeholder="ผล 6 หลัก"
                value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value })}
                required
              />
            ) : null}
            {form.gameType === 'yiki' ? (
              <>
                <select
                  className={inputClass}
                  value={form.roomId}
                  disabled={Boolean(editing)}
                  onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                  required
                >
                  <option value="">เลือกห้อง</option>
                  {formRooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
                <input
                  className={inputClass}
                  maxLength={3}
                  placeholder="ผล 3 หลัก"
                  value={form.result}
                  onChange={(e) => setForm({ ...form, result: e.target.value })}
                  required
                />
              </>
            ) : null}
            {form.gameType === 'soi_dao' ? (
              <>
                <select
                  className={inputClass}
                  value={form.roomId}
                  disabled={Boolean(editing)}
                  onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                  required
                >
                  <option value="">เลือกห้อง</option>
                  {formRooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 md:col-span-4">
                  {form.results.map((value, index) => (
                    <label key={`soi-result-${index}`} className="space-y-1">
                      <span className="block text-[10px] text-gray-500">
                        {selectedSoiRoom?.prizes?.[index] != null
                          ? `$${selectedSoiRoom.prizes[index]}`
                          : `รางวัล #${index + 1}`}
                      </span>
                      <input
                        className={inputClass}
                        maxLength={2}
                        inputMode="numeric"
                        placeholder={`#${index + 1}`}
                        value={value}
                        onChange={(e) => setSoiResult(index, e.target.value)}
                        required
                      />
                    </label>
                  ))}
                </div>
              </>
            ) : null}
            <input
              className={inputClass}
              placeholder="หมายเหตุ"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={saving}>
                {saving ? 'กำลังบันทึก...' : editing ? 'ยืนยันแก้ไข' : 'ล็อกผล'}
              </Button>
              {editing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setForm(emptyForm);
                  }}
                >
                  ยกเลิก
                </Button>
              ) : null}
            </div>
          </form>
          {error ? <p className="mt-3 text-sm text-brand-red">{error}</p> : null}
          {message ? <p className="mt-3 text-sm text-green-700">{message}</p> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>รายการล็อกผล</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRefreshKey((key) => key + 1)}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              รีเฟรช
            </Button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <select
              className={inputClass}
              value={filters.gameType}
              onChange={(e) => changeFilter('gameType', e.target.value)}
            >
              <option value="">ทุกเกม</option>
              <option value="lottery">Lottery</option>
              <option value="lao">หวยลาว</option>
              <option value="yiki">Yiki</option>
              <option value="soi_dao">Soi Dao</option>
            </select>
            <select
              className={inputClass}
              value={filters.roomId}
              onChange={(e) => changeFilter('roomId', e.target.value)}
            >
              <option value="">ทุกห้อง</option>
              {(filters.gameType ? filterRooms : allRooms).map((room) => (
                <option key={`${room.id}-${room.name}`} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
            <select
              className={inputClass}
              value={filters.status}
              onChange={(e) => changeFilter('status', e.target.value)}
            >
              <option value="">ทุกสถานะ</option>
              <option value="locked">รอออก</option>
              <option value="consumed">ออกแล้ว</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="px-2 py-2">เกม / ห้อง</th>
                <th className="px-2 py-2">งวด / รอบ</th>
                <th className="px-2 py-2">ผล</th>
                <th className="px-2 py-2">สถานะ</th>
                <th className="px-2 py-2">ผู้ล็อก / เวลา</th>
                <th className="px-2 py-2">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {(rows?.items || []).map((lock) => (
                <tr key={lock.id} className="border-b border-gray-100">
                  <td className="px-2 py-2">
                    {lock.gameType}
                    {lock.roomId ? ` · ${lock.roomId}` : ''}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">
                    {lock.targetKey}
                  </td>
                  <td className="px-2 py-2 font-mono font-bold">
                    {resultLabel(lock)}
                  </td>
                  <td className="px-2 py-2">{lock.status}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-600">
                    {lock.createdBy}
                    <br />
                    {new Date(lock.createdAt).toLocaleString('th-TH')}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      {lock.status === 'locked' ? (
                        <Button variant="outline" size="sm" onClick={() => startEdit(lock)}>
                          <Edit3 className="h-3 w-3" />
                        </Button>
                      ) : null}
                      <Button variant="ghost" size="sm" onClick={() => showAudits(lock)}>
                        <History className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AdminPagination
            offset={offset}
            limit={PAGE_SIZE}
            total={rows?.total || 0}
            loading={loading}
            onOffsetChange={setOffset}
          />
        </CardContent>
      </Card>

      {audits ? (
        <Card>
          <CardHeader>
            <CardTitle>Audit ล็อก #{audits.lock.id}</CardTitle>
          </CardHeader>
          <CardContent>
            {audits.items.length ? (
              <div className="space-y-3">
                {audits.items.map((audit) => (
                  <div key={audit.id} className="rounded-lg border p-3 text-sm">
                    <p className="font-semibold">
                      {audit.changedBy} ·{' '}
                      {new Date(audit.changedAt).toLocaleString('th-TH')}
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-gray-600">
                      {JSON.stringify(audit.previous)} → {JSON.stringify(audit.next)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">ยังไม่มีการแก้ไข</p>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
